import { AlertTriangle, Paperclip, Search } from "lucide-react";
import { useNotify } from "ra-core";
import { useState } from "react";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { formatEuro } from "../gulaConstants";
import {
  EXPENSE_CATEGORIES,
  labelFor,
  needsReview,
  round2,
  type AccExpense,
} from "./accounting";
import { spanishDate } from "./gestorPackage";
import { openAccountingFile } from "./useAccountingData";

const normalize = (text: string) =>
  text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/** Purchase invoices of the period, newest first. */
export const ExpensesView = ({
  expenses,
  review,
  onReviewChange,
  onEdit,
}: {
  expenses: AccExpense[];
  review: boolean;
  onReviewChange: (review: boolean) => void;
  onEdit: (expense: AccExpense) => void;
}) => {
  const notify = useNotify();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  const visible = expenses.filter(
    (expense) =>
      (category === "all" || expense.category === category) &&
      (!review || needsReview(expense)) &&
      (!query.trim() ||
        normalize(
          `${expense.supplier_name} ${expense.invoice_number ?? ""} ${expense.description ?? ""}`,
        ).includes(normalize(query.trim()))),
  );
  const totals = visible.reduce(
    (sum, expense) => ({
      base:
        sum.base + Number(expense.base_amount) + Number(expense.other_amount),
      vat: sum.vat + Number(expense.vat_amount),
      total: sum.total + Number(expense.total),
    }),
    { base: 0, vat: 0, total: 0 },
  );

  const open = async (path: string) => {
    try {
      await openAccountingFile(path);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Error", {
        type: "error",
      });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-52 flex-1 sm:max-w-xs">
          <Search className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar proveedor, nº o concepto"
            className="pl-8"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {EXPENSE_CATEGORIES.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => onReviewChange(!review)}
          className={cn(
            "inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm",
            review
              ? "border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-400/15 dark:text-amber-200"
              : "text-muted-foreground hover:bg-muted",
          )}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          Por revisar
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Fecha</th>
              <th className="px-3 py-2 text-left font-medium">Proveedor</th>
              <th className="px-3 py-2 text-left font-medium">Categoría</th>
              <th className="px-3 py-2 text-right font-medium">Base</th>
              <th className="px-3 py-2 text-right font-medium">IVA</th>
              <th className="px-3 py-2 text-right font-medium">Total</th>
              <th className="px-3 py-2 text-left font-medium">Factura</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((expense) => (
              <tr
                key={String(expense.id)}
                onClick={() => onEdit(expense)}
                className="cursor-pointer border-t hover:bg-muted/40"
              >
                <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                  {spanishDate(expense.invoice_date)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1.5 font-medium">
                    {expense.supplier_name}
                    {expense.status === "review" ? (
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-400/15 dark:text-amber-200">
                        Revisar
                      </span>
                    ) : null}
                    {expense.source === "ai" ? (
                      <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[11px] font-normal text-violet-800 dark:bg-violet-400/15 dark:text-violet-200">
                        IA
                      </span>
                    ) : expense.source === "meta_ads" ? (
                      <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[11px] font-normal text-sky-800 dark:bg-sky-400/15 dark:text-sky-200">
                        Meta automático
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[expense.invoice_number, expense.description]
                      .filter(Boolean)
                      .join(" · ") || " "}
                  </div>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {labelFor(EXPENSE_CATEGORIES, expense.category)}
                  {expense.vat_mode === "reverse_charge" ? (
                    <div className="text-xs">Inversión del sujeto pasivo</div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(
                    Number(expense.base_amount) + Number(expense.other_amount),
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(Number(expense.vat_amount))}
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">
                  {formatEuro(Number(expense.total))}
                  {!expense.paid_at ? (
                    <div className="text-xs font-normal text-amber-700 dark:text-amber-300">
                      sin pagar
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  {expense.file_path ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        open(expense.file_path as string);
                      }}
                      className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                      title={expense.file_name ?? "Abrir factura"}
                    >
                      <Paperclip className="h-4 w-4" />
                      Ver
                      {expense.extra_files?.length
                        ? ` +${expense.extra_files.length}`
                        : ""}
                    </button>
                  ) : (
                    <span className="text-xs text-amber-700 dark:text-amber-300">
                      Falta
                    </span>
                  )}
                  {expense.vat_mode !== "exempt" &&
                  !expense.supplier_tax_id?.trim() ? (
                    <div className="text-xs text-amber-700 dark:text-amber-300">
                      Sin NIF
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
            {!visible.length ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-8 text-center text-muted-foreground"
                >
                  {expenses.length
                    ? "Ningún gasto con estos filtros."
                    : "Aún no hay gastos en este periodo."}
                </td>
              </tr>
            ) : null}
          </tbody>
          {visible.length ? (
            <tfoot className="border-t bg-muted/30 font-medium">
              <tr>
                <td className="px-3 py-2" colSpan={3}>
                  {visible.length} {visible.length === 1 ? "gasto" : "gastos"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(round2(totals.base))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(round2(totals.vat))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(round2(totals.total))}
                </td>
                <td />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};
